import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Configuration } from '../Configuration';
import * as _ from 'lodash';
import {
    GetReviewsResponse,
    ReviewDetailResponse,
    CreateNoteResponse,
    UpdateSurveyResponseAnswerRequest,
    MaintenanceTicketResponse,
    GetReviewTicketsResponse,
    SurveyType,
    InitialCountResponse,
    MaintenanceTicketDetailResponse,
    ReviewsBulkResponse,
    ReviewsBulk,
    GetBulkImports,
    GetS3AccessResponse,
    GetTeamDetail,
    NinetyDayAvgResponse,
    AssignResponse,
    PAL_SOURCES,
} from '@reviews/interfaces';
import { flowId, StorageKeys } from '@reviews/frontend';
import type {
    AddFollowUpQueryArg,
    AddHistoryQueryArg,
    AssignUserQueryArg,
    CreateReviewMaintenanceTicketQueryArg,
    EmptyQueryArg,
    Get90DayAvgQueryArg,
    GetInitialCountQueryArg,
    GetS3AccessQueryArg,
    ProcessReviewQueryArg,
    RepublishPalReviewsCountResponse,
    RepublishPalReviewsResponse,
    RefreshMaintenanceTicketQueryArg,
    RepublishPalReviewsQueryArg,
    UploadPropertyManagerConfigQueryArg,
    UploadPropertyManagerConfigResponse,
    QueueUnitMigrationRollbackQueryArg,
    QueueUnitMigrationRollbackResponse,
    BackfillPalUnitReviewSummariesResponse,
    RegenerateUnitReviewSummariesRequest,
    RegenerateUnitReviewSummariesResponse,
    SyncUnitFromPalRequest,
    SyncUnitFromPalResponse,
    UpdateSurveyResponseAnswerQueryArg,
    DeleteReviewContentQueryArg,
    RevalidateGuestContentByIdsQueryArg,
    RevalidateGuestContentCountResponse,
    RevalidateGuestContentResponse,
} from '../types/review-service.type';
import { ReopenReviewQueryArg } from '../types/review-service.type';

const REVIEWS_V2_BASE = `${Configuration.getReviewsManagementAPIUrl()}/management/v2`;

export type ApiRequest = { pageNumber?: number; pageSize?: number; filters?: {}; include?: string; sort?: string };

export enum ResponseType {
    DETAILS = 'DETAILS',
    ERROR = 'ERROR',
    SUCCESS = 'SUCCESS',
    LIST = 'LIST',
}

export const reviewsApi = createApi({
    reducerPath: 'reviewsApi',
    tagTypes: [
        'Reviews',
        'ReviewsV2',
        'ReviewsDetails',
        'InitialCount',
        'Search',
        'MaintenanceTicketDetail',
        'BulkImport',
        'TeamDetailCount',
    ],
    baseQuery: fetchBaseQuery({
        baseUrl: `${Configuration.getReviewsManagementAPIUrl()}/management/v1`,
        prepareHeaders: (headers) => {
            headers.set('Content-Type', 'application/json; charset=UTF-8');
            headers.set('Authorization', `Bearer ${localStorage.getItem(StorageKeys.ACCESS_TOKEN)}`);
            headers.set('x-identity-token', `${localStorage.getItem(StorageKeys.ID_TOKEN)}`);
            headers.set('Access-Control-Allow-Origin', `origin-list`);
            headers.set('X-Flow-Id', flowId);
            headers.set('X-Client-Identifier', 'reviews-management-app');
            return headers;
        },
    }),
    endpoints: (builder) => ({
        getReviews: builder.query<GetReviewsResponse, ApiRequest>({
            query: (req: ApiRequest) => {
                const filters = _.reduce(
                    req.filters,
                    (acc, curr, key) => {
                        acc[`filter${key}`] = curr;
                        return acc;
                    },
                    {} as { [key: string]: any }
                );
                const params = {
                    type: SurveyType.REVIEW,
                    'page[number]': (req.pageNumber && req.pageNumber + 1) || 1,
                    'page[size]': req.pageSize || 10,
                    include: req.include,
                    sort: req.sort,
                    'filter[type]': SurveyType.REVIEW,
                    ...filters,
                };
                return {
                    url: 'reviews/responses',
                    method: 'GET',
                    params,
                };
            },
            providesTags: [{ type: 'Reviews', id: ResponseType.LIST }],
        }),

        getReviewsV2: builder.query<GetReviewsResponse, ApiRequest>({
            query: (req: ApiRequest) => {
                const filters = _.reduce(
                    req.filters,
                    (acc, curr, key) => {
                        acc[`filter${key}`] = curr;
                        return acc;
                    },
                    {} as { [key: string]: any }
                );
                const params = {
                    'page[number]': (req.pageNumber && req.pageNumber + 1) || 1,
                    'page[size]': req.pageSize || 10,
                    include: req.include,
                    sort: req.sort,
                    ...filters,
                };
                return {
                    url: `${REVIEWS_V2_BASE}/reviews/responses`,
                    method: 'GET',
                    params,
                };
            },
            providesTags: [{ type: 'ReviewsV2', id: ResponseType.LIST }],
        }),

        getBulkImports: builder.query<GetBulkImports, ApiRequest>({
            query: (req: ApiRequest) => {
                const filters = _.reduce(
                    req.filters,
                    (acc, curr, key) => {
                        acc[`filter[${key}]`] = curr;
                        return acc;
                    },
                    {} as { [key: string]: any }
                );
                const params = {
                    'page[number]': (req.pageNumber && req.pageNumber + 1) || 1,
                    'page[size]': req.pageSize || 10,
                    include: req.include,
                    sort: req.sort,
                    ...filters,
                };
                return {
                    url: 'reviews/bulk/imports',
                    method: 'GET',
                    params,
                };
            },
            providesTags: ['BulkImport'],
        }),

        getAllTicketReviews: builder.query<GetReviewTicketsResponse, EmptyQueryArg>({
            query: (_req) => {
                return {
                    url: 'reviews/tickets',
                    method: 'GET',
                };
            },
        }),

        getReviewDetails: builder.query<{ data: ReviewDetailResponse }, number>({
            query: (id: number) => ({
                url: `reviews/${id}`,
                method: 'GET',
            }),
            providesTags: [{ type: 'ReviewsDetails', id: ResponseType.DETAILS }],
            // Force fresh data on every mount to ensure 'source' field is always present
            keepUnusedDataFor: 0,
        }),

        getTicketDetail: builder.query<MaintenanceTicketDetailResponse, number>({
            query: (id: number) => ({
                url: `reviews/ticket`,
                method: 'GET',
                params: {
                    'filter[review_id]': id,
                    include: 'created_by',
                },
            }),
            providesTags: [{ type: 'MaintenanceTicketDetail', id: ResponseType.DETAILS }],
        }),

        getInitialCount: builder.query<{ data: InitialCountResponse }, GetInitialCountQueryArg>({
            query: (req: { email: string; palFilter?: boolean }) => {
                const params: any = { 'filter[email]': req.email };
                // PAL toggle: ON = include PAL + PMS External, OFF = exclude both
                const palSources = PAL_SOURCES;
                if (typeof req.palFilter === 'boolean') {
                    if (req.palFilter) {
                        params['filter[source][in]'] = palSources;
                    } else {
                        params['filter[source][notin]'] = palSources;
                    }
                }
                return {
                    url: 'reviews/counts',
                    method: 'GET',
                    params,
                };
            },
            providesTags: [
                { type: 'InitialCount', id: ResponseType.SUCCESS },
                { type: 'InitialCount', id: ResponseType.LIST },
            ],
        }),

        get90DayAvg: builder.query<{ data: NinetyDayAvgResponse }, Get90DayAvgQueryArg>({
            query: (req: { admin_unit_id: number }) => ({
                url: 'reviews/ninety-day-avg',
                method: 'GET',
                params: { 'filter[admin_unit_id]': req.admin_unit_id },
            }),
        }),

        addHistoryNote: builder.mutation<{ data: CreateNoteResponse }, AddHistoryQueryArg>({
            query: ({ note, id }) => ({
                url: `reviews/${id}/history/note`,
                method: 'POST',
                body: {
                    data: {
                        type: 'create-history-note',
                        attributes: {
                            note,
                        },
                    },
                },
            }),
            invalidatesTags: ['ReviewsDetails'],
        }),

        processReview: builder.mutation<any, ProcessReviewQueryArg>({
            query: (req) => ({
                url: `reviews/${req.id}/process`,
                method: 'PUT',
                body: {
                    data: {
                        type: 'process-review',
                        attributes: {
                            show_rating: req.show_rating,
                            show_review: req.show_review,
                            response: req.response ?? '',
                            validate_assigned: req.validate_assigned,
                            time: req.time,
                        },
                    },
                },
            }),
            invalidatesTags: (_result, error) => [{ type: 'ReviewsDetails', id: error ? ResponseType.ERROR : ResponseType.DETAILS }],
        }),

        updateSurveyResponseAnswer: builder.mutation<{ data: UpdateSurveyResponseAnswerRequest }, UpdateSurveyResponseAnswerQueryArg>({
            query: (req) => ({
                url: `reviews/response/${req.id}`,
                method: 'PUT',
                body: {
                    data: {
                        type: 'survey-response-answer',
                        attributes: {
                            survey_response_answers_id: req.survey_response_answers_id,
                            new_value: req.new_value,
                            old_value: req.old_value,
                            answer: req.answer,
                            attribute: req.attribute,
                        },
                    },
                },
            }),
            invalidatesTags: ['ReviewsDetails'],
        }),

        createReviewMaintenanceTicket: builder.mutation<{ data: MaintenanceTicketResponse }, CreateReviewMaintenanceTicketQueryArg>({
            query: (req) => ({
                url: `reviews/${req.id}/maintenance-ticket`,
                method: 'POST',
                body: {
                    data: {
                        type: 'maintenance-ticket',
                        attributes: {
                            ...req.data,
                            validate_assigned: req.validate_assigned,
                            time: req.time,
                        },
                    },
                },
            }),
            invalidatesTags: (_result, error) => [
                { type: 'ReviewsDetails', id: error ? ResponseType.ERROR : ResponseType.DETAILS },
                { type: 'MaintenanceTicketDetail', id: error ? ResponseType.ERROR : ResponseType.DETAILS },
            ],
        }),

        refreshMaintenanceTicket: builder.mutation<any, RefreshMaintenanceTicketQueryArg>({
            query: (req) => ({
                url: `reviews/ticket/${req.id}/refresh`,
                method: 'PATCH',
                body: {
                    data: {
                        type: 'ticket-refresh',
                        attributes: {},
                    },
                },
            }),
            invalidatesTags: ['MaintenanceTicketDetail'],
        }),
        uploadBulkReview: builder.mutation<{ data: ReviewsBulkResponse }, ReviewsBulk>({
            query: (req: ReviewsBulk) => ({
                url: `/reviews/bulk/imports`,
                method: 'POST',
                body: {
                    data: {
                        type: 'reviews-bulk',
                        attributes: {
                            email: req.email,
                            file: req.file,
                            note: req.note,
                            ...(req.is_pal_bulk_import === true
                                ? {
                                      is_pal_bulk_import: true,
                                      ...(req.pal_unit_identifier ? { pal_unit_identifier: req.pal_unit_identifier } : {}),
                                  }
                                : {}),
                        },
                    },
                },
            }),
            invalidatesTags: ['BulkImport'],
        }),

        assignUser: builder.mutation<{ data: AssignResponse }, AssignUserQueryArg>({
            query: ({ idUser, idsReviews, validation }) => ({
                url: `users/${idUser}/assign/reviews`,
                method: `PATCH`,
                body: {
                    data: {
                        type: 'assign-reviews',
                        attributes: {
                            assigned_reviews_id: idsReviews,
                            validate_assigned: validation,
                        },
                    },
                },
            }),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'Reviews', id: _.isNil(arg?.tag) ? ResponseType.LIST : arg.tag },
                { type: 'InitialCount', id: _.isNil(arg?.tag) ? ResponseType.LIST : arg.tag },
            ],
        }),

        autoAssignUser: builder.mutation<void, { pal_sources_only: boolean }>({
            query: ({ pal_sources_only }) => ({
                url: `users/auto-assign/reviews`,
                method: `PATCH`,
                body: {
                    data: {
                        type: 'auto-assign-reviews',
                        attributes: {
                            pal_sources_only,
                        },
                    },
                },
            }),
            invalidatesTags: (_result, error) => [
                { type: 'Reviews', id: error ? ResponseType.ERROR : ResponseType.LIST },
                { type: 'InitialCount', id: error ? ResponseType.ERROR : ResponseType.SUCCESS },
            ],
        }),

        getS3Access: builder.query<{ data: GetS3AccessResponse }, GetS3AccessQueryArg>({
            query: ({ bulk_import_id }) => ({
                url: `reviews/bulk/imports/${bulk_import_id}/link`,
                method: `GET`,
            }),
        }),

        reopenReview: builder.mutation<any, ReopenReviewQueryArg>({
            query: (req) => ({
                url: `reviews/${req.survey_response_id}/reopen`,
                method: 'PATCH',
                body: {
                    data: {
                        type: 'review-detail',
                        attributes: {
                            time: req.time,
                        },
                    },
                },
            }),
            invalidatesTags: (_result, error) => [{ type: 'ReviewsDetails', id: error ? ResponseType.ERROR : ResponseType.DETAILS }],
        }),

        deleteReviewContent: builder.mutation<void, DeleteReviewContentQueryArg>({
            query: ({ reviewId, contentId }) => ({
                url: `reviews/${reviewId}/contents/${contentId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, error) => [{ type: 'ReviewsDetails', id: error ? ResponseType.ERROR : ResponseType.DETAILS }],
        }),

        revalidateGuestContentCount: builder.mutation<RevalidateGuestContentCountResponse, void>({
            query: () => ({
                url: 'reviews/contents/revalidate/count',
                method: 'GET',
            }),
        }),

        revalidateAllGuestContent: builder.mutation<RevalidateGuestContentResponse, void>({
            query: () => ({
                url: 'reviews/contents/revalidate/all',
                method: 'POST',
                body: {
                    data: {
                        type: 'revalidate-guest-content',
                        attributes: {},
                    },
                },
            }),
        }),

        revalidateGuestContentByIds: builder.mutation<RevalidateGuestContentResponse, RevalidateGuestContentByIdsQueryArg>({
            query: ({ review_content_ids }) => ({
                url: 'reviews/contents/revalidate',
                method: 'POST',
                body: {
                    data: {
                        type: 'revalidate-guest-content',
                        attributes: {
                            review_content_ids,
                        },
                    },
                },
            }),
            invalidatesTags: (_result, error) => [{ type: 'ReviewsDetails', id: error ? ResponseType.ERROR : ResponseType.DETAILS }],
        }),

        republishPublicReviewsWithContent: builder.mutation<RepublishPalReviewsResponse, void>({
            query: () => ({
                url: 'reviews/contents/re-publish',
                method: 'POST',
                body: {
                    data: {
                        type: 'republish-pal-guest-reviews',
                        attributes: {},
                    },
                },
            }),
        }),

        addFollowUp: builder.mutation<any, AddFollowUpQueryArg>({
            query: (data) => ({
                url: `reviews/ticket/${data.id}/followup`,
                method: 'PATCH',
                body: {
                    data: {
                        type: 'add-follow-up',
                        attributes: {
                            note: data.note,
                        },
                    },
                },
            }),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'ReviewsDetails', id: arg.tag },
                { type: 'MaintenanceTicketDetail', id: arg.tag },
                { type: 'Reviews', id: arg.tag },
            ],
        }),

        getTeamDetailCount: builder.query<GetTeamDetail, EmptyQueryArg>({
            query: () => {
                return {
                    url: 'reviews/team-detail',
                    method: 'GET',
                };
            },
            providesTags: ['TeamDetailCount'],
        }),
        republishPalReviewsCount: builder.mutation<RepublishPalReviewsCountResponse, RepublishPalReviewsQueryArg>({
            query: (req) => {
                return {
                    url: 'reviews/pal/re-publish/count',
                    method: 'GET',
                    params: {
                        'filter[admin_unit_id]': req.admin_unit_id,
                        'filter[review_date_from]': req.review_date_from,
                        'filter[review_date_to]': req.review_date_to,
                    },
                };
            },
        }),
        republishPalReviews: builder.mutation<RepublishPalReviewsResponse, RepublishPalReviewsQueryArg>({
            query: (req) => {
                return {
                    url: 'reviews/pal/re-publish',
                    method: 'POST',
                    body: {
                        data: {
                            type: 'republish-pal-guest-reviews',
                            attributes: {
                                admin_unit_id: req.admin_unit_id,
                                review_date_from: req.review_date_from,
                                review_date_to: req.review_date_to,
                            },
                        },
                    },
                };
            },
        }),
        uploadPropertyManagerConfig: builder.mutation<UploadPropertyManagerConfigResponse, UploadPropertyManagerConfigQueryArg>({
            query: (req) => ({
                url: 'reviews/property-manager-config/import',
                method: 'POST',
                body: {
                    data: {
                        type: 'property-manager-config-upload',
                        attributes: {
                            file: req.file,
                            file_name: req.file_name,
                        },
                    },
                },
            }),
        }),
        queueUnitMigrationRollback: builder.mutation<QueueUnitMigrationRollbackResponse, QueueUnitMigrationRollbackQueryArg>({
            query: (req) => ({
                url: 'reviews/property-manager-config/unit-migration-rollback',
                method: 'POST',
                body: {
                    data: {
                        type: 'unit-migration-rollback',
                        attributes: {
                            admin_unit_ids: req.admin_unit_ids,
                        },
                    },
                },
            }),
        }),
        backfillPalUnitReviewSummaries: builder.mutation<BackfillPalUnitReviewSummariesResponse, void>({
            query: () => ({
                url: `${REVIEWS_V2_BASE}/units/review-summary/backfill`,
                method: 'POST',
                body: {
                    data: {
                        type: 'unit-review-summary-backfill',
                        attributes: {
                            started: true,
                        },
                    },
                },
            }),
        }),
        regenerateUnitReviewSummaries: builder.mutation<RegenerateUnitReviewSummariesResponse, RegenerateUnitReviewSummariesRequest>({
            query: (req) => ({
                url: `${REVIEWS_V2_BASE}/units/review-summary/regenerate`,
                method: 'POST',
                body: {
                    data: {
                        type: 'unit-review-summary-regenerate',
                        attributes: {
                            admin_unit_ids: req.admin_unit_ids,
                        },
                    },
                },
            }),
        }),
        syncUnitFromPal: builder.mutation<SyncUnitFromPalResponse, SyncUnitFromPalRequest>({
            query: (req) => ({
                url: `${REVIEWS_V2_BASE}/units/sync-from-pal`,
                method: 'POST',
                body: {
                    data: {
                        type: 'unit-sync-from-pal',
                        attributes: {
                            admin_unit_id: req.admin_unit_id,
                        },
                    },
                },
            }),
        }),
    }),
});

export const {
    useGetReviewsQuery,
    useGetReviewsV2Query,
    useGetReviewDetailsQuery,
    useGetAllTicketReviewsQuery,
    useLazyGetInitialCountQuery,
    useLazyGet90DayAvgQuery,
    useGetTicketDetailQuery,
    useGetBulkImportsQuery,
    useAddHistoryNoteMutation,
    useProcessReviewMutation,
    useUpdateSurveyResponseAnswerMutation,
    useCreateReviewMaintenanceTicketMutation,
    useUploadBulkReviewMutation,
    useAssignUserMutation,
    useAutoAssignUserMutation,
    useLazyGetS3AccessQuery,
    useReopenReviewMutation,
    useDeleteReviewContentMutation,
    useRevalidateGuestContentCountMutation,
    useRevalidateAllGuestContentMutation,
    useRevalidateGuestContentByIdsMutation,
    useRepublishPublicReviewsWithContentMutation,
    useAddFollowUpMutation,
    useLazyGetTeamDetailCountQuery,
    useRefreshMaintenanceTicketMutation,
    useRepublishPalReviewsCountMutation,
    useRepublishPalReviewsMutation,
    useUploadPropertyManagerConfigMutation,
    useQueueUnitMigrationRollbackMutation,
    useBackfillPalUnitReviewSummariesMutation,
    useRegenerateUnitReviewSummariesMutation,
    useSyncUnitFromPalMutation,
} = reviewsApi;
